import { ACTIVE_SUBSCRIPTION_STATUSES } from "@convex/model/subscription";
import type { SubscriptionStatus, SubscriptionTier } from "@convex/validators";
import type { Credits } from "@/types/types";

/**
 * Structural, not `Subscription` (Postgres) or `Doc<"subscriptions">`
 * (Convex) specifically — both shapes satisfy this, so the same functions
 * work for the server callers still on Postgres rows (Task 12 moves those)
 * and the Convex-backed contexts/pages this task cuts over.
 */
type SubscriptionLike = {
  status?: SubscriptionStatus | null;
  tier: SubscriptionTier;
};

/** Re-export of the one definition, in `convex/model/subscription.ts` —
 * `convex/billing.ts` reads the same list, so the two can no longer drift. */
export const activeSubscriptionStatuses: readonly SubscriptionStatus[] =
  ACTIVE_SUBSCRIPTION_STATUSES;

export function hasActiveSubscription(subscription: SubscriptionLike | null) {
  if (!subscription?.status) return false;

  return activeSubscriptionStatuses.includes(subscription.status);
}

export function hasActivePremiumSubscription(
  subscription: SubscriptionLike | null,
) {
  // If doesn't have an active subscription, return false
  if (!hasActiveSubscription(subscription)) return false;

  // Else, if the subscription product is the premium plan, return true
  return subscription?.tier === "premium";
}

/**
 * Checks if a user has enough unified credits.
 * With the unified credits system, remaining_credits holds the total available credits
 * from all non-expired batches (synced from the ledger).
 * @param credits - The user's credits.
 * @param requiredCredits - The number of credits (in seconds) required.
 * @returns True if the user has enough credits, false otherwise.
 */
export function hasEnoughCredits(
  credits: Credits | null,
  requiredCredits: number,
) {
  // With unified credits, remaining_credits holds the total from the ledger sync
  const remainingCredits = credits?.remaining_credits ?? 0;

  return remainingCredits >= requiredCredits;
}
